import { Button, Paper, Stack, styled, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as clientSlice from '../../../data/client.slice';
import dateUtils from '../../../services/DateUtils';
import { DateTime } from 'luxon';
import { useEffect } from 'react';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { upperFirst } from 'lodash';
import { DataGridPro } from '../../tables/DataGridPro';
import { GridColDef } from '@mui/x-data-grid-pro';
import { PassengerIngestionExecutionStatus } from '../../../data/models/passenger-ingestion.model';

const PREFIX = 'PassengerIngestionDetails';

const classes = {
  container: `${PREFIX}-container`,
  header: `${PREFIX}-header`,
  title: `${PREFIX}-title`,
  dataRow: `${PREFIX}-dataRow`,
  dataLabel: `${PREFIX}-dataLabel`,
  dataValue: `${PREFIX}-dataValue`,
  programList: `${PREFIX}-programList`,
  filesHeader: `${PREFIX}-filesHeader`,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  [`& .${classes.container}`]: {
    padding: theme.spacing(3),
  },
  [`& .${classes.header}`]: {
    paddingBottom: theme.spacing(3),
    marginBottom: theme.spacing(1),
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontWeight: 700,
    fontSize: 30,
  },
  [`& .${classes.dataRow}`]: {
    gap: theme.spacing(1),
  },
  [`& .${classes.dataLabel}`]: {
    fontWeight: 'bold',
  },
  [`& .${classes.programList}`]: {
    margin: 0,
  },
  [`& .${classes.filesHeader}`]: {
    marginTop: theme.spacing(2),
  },
}));

export default function PassengerIngestionDetails() {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const clients = useAppSelector(clientSlice.selectClients);
  const execution = useAppSelector((state) =>
    clientSlice.selectPassengerIngestionExecution(state, id ?? ''),
  );
  const clientBC = searchParams.get('clientBC');

  useEffect(() => {
    dispatch(clientSlice.fetchClientsAsync());

    if (clientBC) {
      dispatch(clientSlice.fetchPassengerIngestionExecutionsAsync(clientBC));
    }
  }, []);

  const clientName = clients.find(
    (client) => client.clientBC === clientBC,
  )?.legalName;

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('passengerIngestion.name'),
      sortable: true,
      minWidth: 250,
    },
    {
      field: 'type',
      headerName: t('passengerIngestion.type'),
      sortable: false,
    },
    {
      field: 'time',
      headerName: t('passengerIngestion.uploadTime'),
      minWidth: 250,
      sortable: false,
      valueFormatter: (params) =>
        dateUtils.formatDateTime(DateTime.fromISO(params.value)),
    },
  ];

  const handleRestartClick = () => {
    //TODO
  };

  return (
    <StyledPaper elevation={2}>
      <Stack direction="column" className={classes.container}>
        <Stack direction="row" className={classes.header}>
          <Typography variant="h3" className={classes.title}>
            {t('passengerIngestion.passengerIngestionDetailsTitle', { id })}
          </Typography>
          {execution?.status === PassengerIngestionExecutionStatus.Failed && (
            <Button
              color="primary"
              variant="contained"
              onClick={() => handleRestartClick()}>
              {t('passengerIngestion.restart')}
            </Button>
          )}
        </Stack>
        <Stack direction="row" className={classes.dataRow}>
          <Typography className={classes.dataLabel}>
            {t('passengerIngestion.client')}:
          </Typography>
          <Typography>{clientName}</Typography>
        </Stack>
        <Stack direction="row" className={classes.dataRow}>
          <Typography className={classes.dataLabel}>
            {t('passengerIngestion.time')}:
          </Typography>
          <Typography>
            {dateUtils.formatDateTime(DateTime.fromISO(execution?.time ?? ''))}
          </Typography>
        </Stack>
        <Stack direction="row" className={classes.dataRow}>
          <Typography className={classes.dataLabel}>
            {t('passengerIngestion.step')}:
          </Typography>
          <Typography>{execution?.step}</Typography>
        </Stack>
        <Stack direction="row" className={classes.dataRow}>
          <Typography className={classes.dataLabel}>
            {t('passengerIngestion.status')}:
          </Typography>
          <Typography>{upperFirst(execution?.status)}</Typography>
        </Stack>
        {execution?.errorMessage && (
          <Stack direction="row" className={classes.dataRow}>
            <Typography className={classes.dataLabel}>
              {t('passengerIngestion.errorMessage')}:
            </Typography>
            <Typography>{execution.errorMessage}</Typography>
          </Stack>
        )}
        <Stack direction="row" className={classes.dataRow}>
          <Typography className={classes.dataLabel}>
            {t('passengerIngestion.totalPassengerRecords')}:
          </Typography>
          <Typography>{execution?.passengerRecordCount}</Typography>
        </Stack>
        <Stack direction="row" className={classes.dataRow}>
          <Typography className={classes.dataLabel}>
            {t('passengerIngestion.unmatchedServicePlans')}:
          </Typography>
          <Typography>{execution?.unmatchedCount}</Typography>
        </Stack>
        <Typography className={classes.dataLabel}>
          {t('passengerIngestion.passengersByProgram')}:
        </Typography>
        <ul className={classes.programList}>
          {execution?.programs.map((program) => {
            return (
              <li>
                {program.name}: {program.passengerCount}
              </li>
            );
          })}
        </ul>
        <Typography className={`${classes.dataLabel} ${classes.filesHeader}`}>
          {t('passengerIngestion.files')}
        </Typography>
        {execution?.files && (
          <DataGridPro
            disableColumnMenu={true}
            getRowId={(row) => row.name}
            rows={execution.files}
            columns={columns}
            rowCount={execution.files.length}
            pagination={true}
            autoHeight
          />
        )}
      </Stack>
    </StyledPaper>
  );
}
